import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { Card, Pill, SectionLabel, Metric } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, BookOpen } from "lucide-react";

export default function Journal() {
  const { data, refresh } = usePolling(async () => (await api.get("/journal")).data, 0, []);
  const { data: live, refresh: refreshLive } = usePolling(async () => (await api.get("/journal/live-stats")).data, 0, []);
  const [open, setOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);
  const entries = data?.entries || [];
  const refreshAll = () => { refresh(); refreshLive(); };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3"><BookOpen className="text-aurum" /><h1 className="text-2xl font-bold tracking-tight">Trading Journal</h1></div>
        <ManualEntry open={open} setOpen={setOpen} onDone={refreshAll} />
      </div>

      {live && !live.empty && (
        <Card className="p-4" data-testid="live-stats-card">
          <SectionLabel>Live MT5 Performance</SectionLabel>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Metric label="Trades" value={live.total_trades} />
            <Metric label="Win rate" value={`${live.win_rate}%`} />
            <Metric label="Net P/L" value={`$${live.total_pnl}`} color={live.total_pnl >= 0 ? "#21C58E" : "#EF6262"} />
            <Metric label="Avg R" value={live.avg_r ?? "—"} />
            <Metric label="Open" value={live.open_count} color={live.open_count ? "#F5B942" : undefined} />
          </div>
        </Card>
      )}
      {live && live.empty && live.open_count > 0 && (
        <Card className="p-3 text-xs text-[var(--text-muted)]" data-testid="open-trades-note">
          You have {live.open_count} open live trade{live.open_count === 1 ? "" : "s"}. Close them with the result to build your live performance stats.
        </Card>
      )}

      {entries.length === 0 ? (
        <Card className="p-10 text-center">
          <div className="text-sm font-semibold text-[var(--text-sec)]">No journal entries yet</div>
          <p className="mt-1 text-xs text-[var(--text-muted)]">Approve a paper trade or add a manual entry. Closed trades are journaled automatically.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => (
            <Card key={e.id || e.trade_id} className="p-4" data-testid="journal-entry">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Pill color={e.direction === "LONG" ? "#21C58E" : "#EF6262"}>{e.direction}</Pill>
                  <span className="text-sm font-medium">{e.strategy}</span>
                  <span className="text-[11px] text-[var(--text-muted)]">{e.regime}</span>
                  <Pill color={e.source === "MT5 LIVE" ? "#D6AE57" : "#98A2B3"}>{e.source}</Pill>
                  {e.status === "OPEN" && <Pill color="#F5B942">OPEN</Pill>}
                </div>
                <div className="flex items-center gap-3 tnum text-sm">
                  {e.result && <span className={`font-semibold ${e.result === "WIN" ? "text-bull" : e.result === "LOSS" ? "text-bear" : ""}`}>{e.result}</span>}
                  {e.pnl != null && <span className={e.pnl >= 0 ? "text-bull" : "text-bear"}>${e.pnl}</span>}
                  {e.r_multiple != null && <span className="text-[var(--text-muted)]">{e.r_multiple}R</span>}
                  {e.status === "OPEN" && (
                    <button onClick={() => setCloseTarget(e)} data-testid="close-trade-btn"
                      className="rounded-full border border-aurum text-aurum px-3 py-1 text-xs font-semibold hover:bg-[rgba(214,174,87,0.1)]">
                      Close trade
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-[var(--text-sec)] tnum">
                {e.entry != null && <span>Entry {e.entry}</span>}
                {e.stop != null && <span>Stop {e.stop}</span>}
                {e.tp1 != null && <span>TP1 {e.tp1}</span>}
                {e.exit_price != null ? <span>Exit {e.exit_price}</span> : e.lots != null && <span>{e.lots} lots</span>}
              </div>
              {(e.user_reasoning || e.lessons || e.mistakes) && (
                <div className="mt-2 text-xs text-[var(--text-muted)] space-y-1">
                  {e.user_reasoning && <div>Reasoning: {e.user_reasoning}</div>}
                  {e.lessons && <div>Lessons: {e.lessons}</div>}
                  {e.mistakes && <div>Mistakes: {e.mistakes}</div>}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
      <CloseTradeDialog entry={closeTarget} onClose={() => setCloseTarget(null)} onDone={refreshAll} />
    </div>
  );
}

function CloseTradeDialog({ entry, onClose, onDone }) {
  const [f, setF] = useState({ result: "WIN", exit_price: "", pnl: "", r_multiple: "", lessons: "" });
  const submit = async () => {
    if (f.pnl === "") { toast.error("Enter your P/L."); return; }
    try {
      await api.post(`/journal/${entry.id}/close`, {
        result: f.result, exit_price: Number(f.exit_price) || null,
        pnl: Number(f.pnl), r_multiple: Number(f.r_multiple) || null, lessons: f.lessons || null,
      });
      toast.success("Trade closed & logged.");
      onClose(); onDone();
      setF({ result: "WIN", exit_price: "", pnl: "", r_multiple: "", lessons: "" });
    } catch { toast.error("Could not close trade."); }
  };
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--card2)] border-[var(--border2)] text-[var(--text)]" data-testid="close-trade-dialog">
        <DialogHeader><DialogTitle>Close {entry?.direction} · {entry?.strategy}</DialogTitle></DialogHeader>
        <p className="text-xs text-[var(--text-muted)] -mt-2">Enter the actual outcome from your MT5 trade.</p>
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Result" value={f.result} onChange={(v) => setF({ ...f, result: v })} options={["WIN", "LOSS", "BREAKEVEN"]} />
          <Inp label="Exit price" value={f.exit_price} onChange={(v) => setF({ ...f, exit_price: v })} />
          <Inp label="P/L ($)" value={f.pnl} onChange={(v) => setF({ ...f, pnl: v })} />
          <Inp label="R multiple" value={f.r_multiple} onChange={(v) => setF({ ...f, r_multiple: v })} />
        </div>
        <textarea placeholder="Lessons from this trade…" value={f.lessons} onChange={(e) => setF({ ...f, lessons: e.target.value })}
          className="w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" rows={2} />
        <button onClick={submit} data-testid="confirm-close-trade" className="btn-gold rounded-lg py-2.5 text-sm font-semibold hover:brightness-110">Close trade</button>
      </DialogContent>
    </Dialog>
  );
}

function ManualEntry({ open, setOpen, onDone }) {
  const [f, setF] = useState({ direction: "LONG", strategy: "Trend Pullback", entry: "", stop: "", tp1: "", lots: "", result: "WIN", pnl: "", r_multiple: "", user_reasoning: "" });
  const submit = async () => {
    try {
      await api.post("/journal", { ...f, entry: Number(f.entry) || 0, stop: Number(f.stop) || null, tp1: Number(f.tp1) || null, lots: Number(f.lots) || null, pnl: Number(f.pnl) || null, r_multiple: Number(f.r_multiple) || null });
      toast.success("Journal entry added."); setOpen(false); onDone();
    } catch { toast.error("Could not add entry."); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="add-journal-btn" className="btn-gold flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold hover:brightness-110"><Plus size={16} /> Add manual trade</button>
      </DialogTrigger>
      <DialogContent className="bg-[var(--card2)] border-[var(--border2)] text-[var(--text)]">
        <DialogHeader><DialogTitle>Log a manual trade</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Sel label="Direction" value={f.direction} onChange={(v) => setF({ ...f, direction: v })} options={["LONG", "SHORT"]} />
          <Sel label="Result" value={f.result} onChange={(v) => setF({ ...f, result: v })} options={["WIN", "LOSS", "BREAKEVEN"]} />
          <Inp label="Strategy" value={f.strategy} onChange={(v) => setF({ ...f, strategy: v })} />
          <Inp label="Entry" value={f.entry} onChange={(v) => setF({ ...f, entry: v })} />
          <Inp label="Stop" value={f.stop} onChange={(v) => setF({ ...f, stop: v })} />
          <Inp label="TP1" value={f.tp1} onChange={(v) => setF({ ...f, tp1: v })} />
          <Inp label="Lots" value={f.lots} onChange={(v) => setF({ ...f, lots: v })} />
          <Inp label="P/L ($)" value={f.pnl} onChange={(v) => setF({ ...f, pnl: v })} />
        </div>
        <textarea placeholder="Reasoning / lessons…" value={f.user_reasoning} onChange={(e) => setF({ ...f, user_reasoning: e.target.value })}
          className="w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum" rows={2} />
        <button onClick={submit} className="btn-gold rounded-lg py-2.5 text-sm font-semibold hover:brightness-110">Save entry</button>
      </DialogContent>
    </Dialog>
  );
}

function Inp({ label, value, onChange }) {
  return <div><label className="text-xs text-[var(--text-sec)]">{label}</label><input value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum tnum" /></div>;
}
function Sel({ label, value, onChange, options }) {
  return <div><label className="text-xs text-[var(--text-sec)]">{label}</label><select value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full rounded-lg bg-[var(--surface)] border border-[var(--border2)] px-3 py-2 text-sm outline-none focus:border-aurum">{options.map((o) => <option key={o} value={o}>{o}</option>)}</select></div>;
}
